import { useMemo, useState } from "react";
import RootAdmin from "../../components/layouts/RootAdmin";
import HeaderPage from "../../components/elements/HeaderPage";
import { Card, CardBody, CardHeader, Chip, Divider, Input, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from "@nextui-org/react";
import useSWR from "swr";
import { getFetcher } from "../../services/api";

// Seluruh panel halaman ini memakai endpoint /dashboard/koordinator/*, yang
// menerima tanggal ISO 8601 - sama dengan keluaran <input type="date">, jadi
// tidak ada konversi format yang perlu dilakukan. Dulu halaman ini menarik dari
// tiga keluarga endpoint dengan tiga format tanggal berbeda.
/**
 * Kerangka bersama ketiga panel atas.
 *
 * Ketiganya memakai susunan yang sama persis - baris keterangan, angka besar,
 * label, dua kotak, lalu baris kaki - supaya elemen yang sejenis berada pada
 * ketinggian yang sama antar kartu. Baris keterangan dan baris kaki tetap
 * digambar walau kosong; tanpa itu kartu yang isinya lebih pendek akan menggeser
 * angka besarnya ke atas dan panelnya terlihat tidak sejajar.
 */
function IsiPanel({ keterangan, angka, label, kotak, kaki, memuat }) {
  if (memuat) {
    return <p className="text-sm text-ink-500">Memuat...</p>;
  }

  return (
    <div className="flex h-full flex-col">
      {/* Dua baris: pada kolom sempit keterangan ini membungkus, dan tinggi
          tetap satu baris membuatnya tertimpa angka besar di bawahnya. */}
      <p className="min-h-[32px] text-xs leading-4 text-ink-500">{keterangan}</p>

      <p className="mt-1 text-3xl font-bold leading-none">{angka}</p>
      <p className="mt-1 text-sm text-ink-500">{label}</p>

      <div className="mt-3 grid grid-cols-2 gap-2">
        {kotak.map((k) => (
          <div
            key={k.label}
            className={`rounded-ds-md border p-2 ${
              k.sorot ? "border-warning-line bg-warning-bg" : k.baik ? "border-success-line bg-success-bg" : "border-line bg-surface-2"
            }`}
          >
            <p className="text-xs text-ink-500">{k.label}</p>
            <p
              className={`text-xl font-semibold ${
                k.sorot ? "text-warning" : k.baik ? "text-success" : "text-g-700"
              }`}
            >
              {k.nilai}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-auto pt-3 min-h-[28px]">{kaki}</div>
    </div>
  );
}

/**
 * Panel Registrasi B3 dan Rekomendasi Pengangkutan B3.
 *
 * Dua angka yang paling dicari koordinator ditaruh di kotak: berapa permohonan
 * yang MASIH BERJALAN pada rentang itu, dan berapa di antaranya yang sudah lewat
 * dua HARI KERJA sejak diajukan - yang terakhir bersorot peringatan bila lebih
 * dari nol.
 */
function KartuPermohonan({ data, memuat }) {
  const lewat = data?.lewatDuaHari ?? 0;

  return (
    <IsiPanel
      memuat={memuat && !data}
      keterangan="Sesuai rentang tanggal di atas"
      angka={data?.total ?? 0}
      label="Total permohonan"
      kotak={[
        { label: "Masih berjalan", nilai: data?.masihOpen ?? 0 },
        { label: "Lewat 2 hari kerja", nilai: lewat, sorot: lewat > 0 },
      ]}
      kaki={
        <div className="flex flex-wrap gap-2">
          <Chip size="sm" color="primary" variant="flat">Proses: {data?.proses ?? 0}</Chip>
          <Chip size="sm" color="success" variant="flat">Selesai: {data?.selesai ?? 0}</Chip>
          <Chip size="sm" color="danger" variant="flat">Ditolak: {data?.ditolak ?? 0}</Chip>
        </div>
      }
    />
  );
}

/**
 * Panel Pelaporan.
 *
 * Angkanya melekat pada PERIODE PELAPORAN yang sedang berjalan, bukan pada
 * rentang tanggal penyaring - kewajiban pelaporan memang dihitung per periode,
 * jadi nama periodenya ditulis di baris keterangan.
 */
function KartuPelaporan({ data, memuat }) {
  const sudah = data?.perusahaanSudahLapor ?? 0;
  const belum = data?.perusahaanBelumLapor ?? 0;
  const wajib = data?.perusahaanWajibLapor ?? 0;
  const persen = wajib > 0 ? Math.round((sudah / wajib) * 100) : 0;

  return (
    <IsiPanel
      memuat={memuat && !data}
      keterangan={`Periode ${data?.periode?.name ?? "belum ada"}`}
      angka={data?.laporanMasuk ?? 0}
      label="Laporan masuk"
      kotak={[
        { label: "Sudah melapor", nilai: sudah, baik: sudah > 0 },
        { label: "Belum melapor", nilai: belum, sorot: belum > 0 },
      ]}
      kaki={
        <p className="text-xs text-ink-500">
          {wajib} perusahaan wajib lapor ({persen}% terpenuhi) dari{" "}
          {data?.totalPerusahaanSimpel ?? 0} perusahaan SIMPEL
        </p>
      }
    />
  );
}

export default function DashboardKoordinatorPage() {
  const todayIso = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState({ startDate: "", endDate: todayIso });

  const koordinatorParams = useMemo(() => {
    // DashboardKoordinatorDto pakai @IsDateString() - string kosong akan gagal
    // validasi, jadi param hanya disertakan kalau memang terisi.
    const params = new URLSearchParams();
    if (date.startDate) params.set("startDate", date.startDate);
    if (date.endDate) params.set("endDate", date.endDate);
    return params.toString();
  }, [date]);

  /*
   * Tiga panel atas kini dari SATU endpoint dengan satu rentang tanggal.
   * Sebelumnya masing-masing menarik dari keluarga endpoint berbeda, dan status
   * registrasi di sana tidak menyaring tanggal sama sekali - kartunya bisa
   * menulis "48 total permohonan" berdampingan dengan "Masuk: 6420".
   */
  const { data: ringkasan, isLoading: loadingRingkasan } = useSWR(`/api/dashboard/koordinator/ringkasan?${koordinatorParams}`, getFetcher);

  const { data: progresRegistrasi, isLoading: loadingProgresRegistrasi } = useSWR(`/api/dashboard/koordinator/delegasi-progress/registrasi?${koordinatorParams}`, getFetcher);
  const { data: progresRekomendasi, isLoading: loadingProgresRekomendasi } = useSWR(`/api/dashboard/koordinator/delegasi-progress/rekomendasi?${koordinatorParams}`, getFetcher);

  const ringkas = ringkasan?.responseData;
  const progresRegistrasiRows = progresRegistrasi?.responseData || [];
  const progresRekomendasiRows = progresRekomendasi?.responseData || [];

  return (
    <RootAdmin>
      <HeaderPage title="Dashboard Koordinator" />

      <Card className="w-full mt-3" radius="sm">
        <CardBody className="flex flex-row items-end gap-3 p-4">
          <Input type="date" label="Tanggal Mulai" size="sm" value={date.startDate} onChange={(e) => setDate((prev) => ({ ...prev, startDate: e.target.value }))} />
          <Input type="date" label="Tanggal Akhir" size="sm" value={date.endDate} onChange={(e) => setDate((prev) => ({ ...prev, endDate: e.target.value }))} />
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 items-stretch">
        <Card radius="sm" className="h-full">
          <CardHeader className="min-h-[76px] items-start">
            <p className="text-md">Registrasi B3</p>
          </CardHeader>
          <Divider />
          <CardBody className="gap-2">
            <KartuPermohonan data={ringkas?.registrasi} memuat={loadingRingkasan} />
          </CardBody>
        </Card>

        <Card radius="sm" className="h-full">
          <CardHeader className="min-h-[76px] items-start">
            <p className="text-md">Rekomendasi Pengangkutan B3</p>
          </CardHeader>
          <Divider />
          <CardBody className="gap-2">
            <KartuPermohonan data={ringkas?.rekomendasi} memuat={loadingRingkasan} />
          </CardBody>
        </Card>

        <Card radius="sm" className="h-full">
          <CardHeader className="min-h-[76px] items-start">
            <p className="text-md">Pelaporan</p>
          </CardHeader>
          <Divider />
          <CardBody className="gap-2">
            <KartuPelaporan data={ringkas?.pelaporan} memuat={loadingRingkasan} />
          </CardBody>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3 mb-6">
        <Card radius="sm">
          <CardHeader className="min-h-[76px] items-start">
            <p className="text-md">Progres Delegasi - Registrasi B3</p>
          </CardHeader>
          <Divider />
          <CardBody>
            <Table aria-label="Progres delegasi registrasi" removeWrapper>
              <TableHeader>
                <TableColumn>PIC</TableColumn>
                <TableColumn>Total</TableColumn>
                <TableColumn>Proses</TableColumn>
                <TableColumn>Selesai</TableColumn>
                <TableColumn>Ditolak</TableColumn>
              </TableHeader>
              <TableBody isLoading={loadingProgresRegistrasi} emptyContent="Belum ada data" items={progresRegistrasiRows}>
                {(row) => (
                  <TableRow key={row.userId || "none"}>
                    <TableCell>{row.fullName}</TableCell>
                    <TableCell>{row.total}</TableCell>
                    <TableCell>{row.proses}</TableCell>
                    <TableCell>{row.selesai}</TableCell>
                    <TableCell>{row.ditolak}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardBody>
        </Card>

        <Card radius="sm">
          <CardHeader className="min-h-[76px] items-start">
            <p className="text-md">Progres Delegasi - Rekomendasi Pengangkutan B3</p>
          </CardHeader>
          <Divider />
          <CardBody>
            <Table aria-label="Progres delegasi rekomendasi" removeWrapper>
              <TableHeader>
                <TableColumn>PIC</TableColumn>
                <TableColumn>Total</TableColumn>
                <TableColumn>Proses</TableColumn>
                <TableColumn>Selesai</TableColumn>
                <TableColumn>Ditolak</TableColumn>
              </TableHeader>
              <TableBody isLoading={loadingProgresRekomendasi} emptyContent="Belum ada data" items={progresRekomendasiRows}>
                {(row) => (
                  <TableRow key={row.userId || "none"}>
                    <TableCell>{row.fullName}</TableCell>
                    <TableCell>{row.total}</TableCell>
                    <TableCell>{row.proses}</TableCell>
                    <TableCell>{row.selesai}</TableCell>
                    <TableCell>{row.ditolak}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      </div>
    </RootAdmin>
  );
}
